import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Row,
  Col,
  Table,
  Empty,
  Spin,
  Tooltip,
  DatePicker,
  InputNumber
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  BookOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CourseList, CourseFilters, courseAPI } from '../../services/courseAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import CourseForm from './CourseForm';

const { Search } = Input;
const { RangePicker } = DatePicker;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface CoursesListProps {
  selectedCompany: SelectedCompany | null;
}

const CoursesList: React.FC<CoursesListProps> = ({ selectedCompany }) => {
  const [courses, setCourses] = useState<CourseList[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CourseList | null>(null);
  const [filters, setFilters] = useState<CourseFilters>({});

  // Rəqəm formatlaşdırma funksiyası
  const formatPrice = (price: number | string | null | undefined): string => {
    // Əgər price yoxdursa və ya null-dursa
    if (!price && price !== 0) return '0';
    
    // String-i number-ə çevir
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Əgər NaN-dırsa və ya number deyilsə
    if (isNaN(numPrice) || typeof numPrice !== 'number') return '0';
    
    // Əgər 0-dırsa
    if (numPrice === 0) return '0';
    
    // Ondalık hissəsi varsa, onu göstər, yoxsa tam rəqəm göstər
    const formatted = numPrice.toFixed(2);
    
    // Sonda olan sıfırları sil
    return formatted.replace(/\.?0+$/, '');
  };

  useEffect(() => {
    if (selectedCompany) {
      loadCourses();
    }
  }, [selectedCompany, filters]);

  const loadCourses = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const response = await courseAPI.getCourses(selectedCompany.id, filters);
      
      if (response.success && response.data) {
        // Data-nın array olduğunu təmin et
        const courseData = Array.isArray(response.data) ? response.data : [];
        setCourses(courseData);
      } else {
        message.error(response.message || 'Kurslar yüklənmədi');
        setCourses([]); // Xəta halında boş array
      }
    } catch (error) {
      console.error('Kurslar yüklənmədi:', error);
      message.error('Kurslar yüklənmədi');
      setCourses([]); // Xəta halında boş array
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCourse(null);
    setIsModalVisible(true);
  };

  const handleEdit = (course: CourseList) => {
    setEditingCourse(course);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number, courseName: string) => {
    try {
      const response = await courseAPI.deleteCourse(id);
      if (response.success) {
        message.success(response.message || 'Kurs silindi');
        loadCourses();
      } else {
        message.error(response.message || 'Kurs silinmədi');
      }
    } catch (error) {
      console.error('Kurs silinmədi:', error);
      message.error('Kurs silinmədi');
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingCourse(null);
  };

  const handleFormSuccess = () => {
    setIsModalVisible(false);
    setEditingCourse(null);
    loadCourses();
  };

  const getStatistics = () => {
    if (!Array.isArray(courses)) {
      return { totalCourses: 0, activeCourses: 0, totalStudents: 0, totalPrice: 0 };
    }
    
    const totalCourses = courses.length;
    const activeCourses = courses.filter(c => c.is_active).length;
    const totalStudents = courses.reduce((sum, c) => sum + (c.student_count || 0), 0);
    const totalPrice = courses.reduce((sum, c) => {
      const price = c.price;
      if (!price && price !== 0) return sum;
      const numPrice = typeof price === 'string' ? parseFloat(price) : price;
      return sum + (isNaN(numPrice) ? 0 : numPrice);
    }, 0);

    return { totalCourses, activeCourses, totalStudents, totalPrice };
  };

  const { totalCourses, activeCourses, totalStudents, totalPrice } = getStatistics();

  const columns: ColumnsType<CourseList> = [
    {
      title: 'Kurs Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: CourseList) => (
        <div>
          <div className="font-medium text-gray-900">{text}</div>
          <div className="text-sm text-gray-500">{record.company_name}</div>
        </div>
      ),
    },
    {
      title: 'Tarixlər',
      key: 'dates',
      render: (_, record: CourseList) => (
        <div className="text-sm">
          {record.start_date && (
            <div className="flex items-center text-green-600">
              <CalendarOutlined className="mr-1" />
              Başlama: {new Date(record.start_date).toLocaleDateString('az-AZ')}
            </div>
          )}
          {record.end_date && (
            <div className="flex items-center text-red-600">
              <CalendarOutlined className="mr-1" />
              Bitmə: {new Date(record.end_date).toLocaleDateString('az-AZ')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Qiymət',
      dataIndex: 'price',
      key: 'price',
      render: (price: number | null) => (
        <div className="flex items-center">
          <DollarOutlined className="mr-1 text-green-500" />
          {price ? `${formatPrice(price)} AZN` : 'Təyin edilməyib'}
        </div>
      ),
    },
    {
      title: 'Fənnlər',
      dataIndex: 'subject_count',
      key: 'subject_count',
      render: (count: number, record: CourseList) => (
        <div>
          <div className="flex items-center">
            <BookOutlined className="mr-1 text-blue-500" />
            {count} fənn
          </div>
          {record.subjects_names.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {record.subjects_names.join(', ')}
              {record.subject_count > 3 && '...'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tələbələr',
      key: 'students',
      render: (_, record: CourseList) => (
        <div>
          <div className="flex items-center">
            <UserOutlined className="mr-1 text-orange-500" />
            <span className="font-medium">{record.student_count || 0}</span>
            <span className="ml-1 text-gray-600">tələbə</span>
          </div>
          {record.active_students_count !== record.student_count && (
            <div className="text-xs text-green-600 mt-1">
              <span className="font-medium">{record.active_students_count || 0}</span> aktiv
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktiv' : 'Deaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (_, record: CourseList) => (
        <Space size="small">
          <Tooltip title="Ətraflı bax">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                // TODO: Kurs detallarını göstər
                message.info('Kurs detalları tezliklə əlavə ediləcək');
              }}
            />
          </Tooltip>
          <Tooltip title="Redaktə et">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Kursu silmək"
            description={`${record.name} kursunu silmək istədiyinizə əminsiniz?`}
            onConfirm={() => handleDelete(record.id, record.name)}
            okText="Bəli"
            cancelText="Xeyr"
            okType="danger"
          >
            <Tooltip title="Sil">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <BookOutlined className="text-6xl text-gray-300 mb-4" />
        <h3 className="text-xl text-gray-500 mb-2">Şirkət seçin</h3>
        <p className="text-gray-400">
          Kursları görmək üçün yuxarıdan şirkət seçin.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOutlined className="mr-3 text-blue-500" />
              Kurslar
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedCompany.name} şirkətinin kursları
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
            className="shadow-sm"
          >
            Yeni Kurs
          </Button>
        </div>

        {/* Statistika kartları */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={6}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{totalCourses}</div>
              <div className="text-gray-600">Ümumi Kurs</div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-green-600">{activeCourses}</div>
              <div className="text-gray-600">Aktiv Kurs</div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{totalStudents}</div>
              <div className="text-gray-600">Ümumi Tələbə</div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-purple-600">
                {formatPrice(totalPrice)} AZN
              </div>
              <div className="text-gray-600">Ümumi Dəyər</div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Axtarış və filtrlər */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Kurs axtar..."
              allowClear
              onSearch={(value) => setFilters({ ...filters, search: value })}
              className="w-full"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Status:</span>
              <Button.Group>
                <Button
                  size="small"
                  type={filters.is_active === undefined ? 'primary' : 'default'}
                  onClick={() => setFilters({ ...filters, is_active: undefined })}
                >
                  Hamısı
                </Button>
                <Button
                  size="small"
                  type={filters.is_active === true ? 'primary' : 'default'}
                  onClick={() => setFilters({ ...filters, is_active: true })}
                >
                  Aktiv
                </Button>
                <Button
                  size="small"
                  type={filters.is_active === false ? 'primary' : 'default'}
                  onClick={() => setFilters({ ...filters, is_active: false })}
                >
                  Deaktiv
                </Button>
              </Button.Group>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Kurslar cədvəli */}
      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={Array.isArray(courses) ? courses : []}
          rowKey="id"
          loading={loading}
          pagination={{
            total: Array.isArray(courses) ? courses.length : 0,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} kurs`,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <p>Hələ heç bir kurs yoxdur</p>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                      İlk kursu əlavə edin
                    </Button>
                  </div>
                }
              />
            ),
          }}
        />
      </Card>

      {/* Modal */}
      {isModalVisible && (
        <CourseForm
          visible={isModalVisible}
          course={editingCourse}
          onCancel={handleModalClose}
          onSuccess={handleFormSuccess}
          selectedCompany={selectedCompany}
        />
      )}
    </div>
  );
};

export default CoursesList;